import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, Users, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, 
         eachDayOfInterval, isSameMonth, isSameDay, isToday, parseISO, addMonths,
         isAfter, isBefore, compareAsc, isPast, parse, isValid } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import type { Property } from '@shared/schema';
import stripeLogo from "@assets/Powered by Stripe - white.png";

interface BookedDirectlyStyleWidgetProps {
  propertyId: string;
  propertyName: string;
  className?: string;
  initialCheckIn?: string;
  initialCheckOut?: string;
  initialGuests?: string;
}

interface CalendarSlot {
  date: string;
  available: boolean;
  price?: number;
  minimum_stay?: number;
  closed_for_arrival?: boolean;
  closed_for_departure?: boolean;
}

interface PricingData {
  totalPrice: string;
  averageNightlyRate: string;
  nights: number;
  currency: string;
  available: boolean;
  fees?: {
    cleaningFee?: number;
    extraGuestCharge?: number;
  };
}

// Cache for calendar data
const calendarCache = new Map<string, { data: CalendarSlot[], timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const bookingTriggerStyles = "hover:border-[var(--toka-primary)] data-[state=open]:border-[var(--toka-primary)] focus-visible:border-[var(--toka-primary)] focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0";
const calendarActionStyles = "px-4 py-2 text-sm text-white bg-toka-secondary border border-gray-300 rounded hover:border-[var(--toka-primary)] focus-visible:border-[var(--toka-primary)] transition-colors";

export default function BookedDirectlyStyleWidget({ 
  propertyId, 
  propertyName, 
  className = "",
  initialCheckIn = "",
  initialCheckOut = "",
  initialGuests = "2"
}: BookedDirectlyStyleWidgetProps) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectingCheckIn, setSelectingCheckIn] = useState(true);
  
  // Refs to always access latest state values (fixes stale closure issues)
  const selectingCheckInRef = useRef(true);
  const checkInDateRef = useRef<Date | null>(null);
  
  // Initialize dates from URL parameters if provided
  const [checkInDate, setCheckInDate] = useState<Date | null>(() => {
    const date = initialCheckIn ? parseISO(initialCheckIn) : null;
    checkInDateRef.current = date;
    return date;
  });
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(() => {
    return initialCheckOut ? parseISO(initialCheckOut) : null;
  });
  
  // Wrapper functions that update both state and ref synchronously
  const updateSelectingCheckIn = (value: boolean) => {
    selectingCheckInRef.current = value;
    setSelectingCheckIn(value);
  };
  
  const updateCheckInDate = (value: Date | null) => {
    checkInDateRef.current = value;
    setCheckInDate(value);
  };
  const [guests, setGuests] = useState(() => {
    return parseInt(initialGuests) || 2;
  });
  
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState<CalendarSlot[]>([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [calendarDataLoaded, setCalendarDataLoaded] = useState(false);
  const [checkInInputValue, setCheckInInputValue] = useState('');
  const [checkOutInputValue, setCheckOutInputValue] = useState('');
  const [checkInInputFocused, setCheckInInputFocused] = useState(false);
  const [checkOutInputFocused, setCheckOutInputFocused] = useState(false);


  // Effect to sync input values when dates are set programmatically (like from URL params)
  useEffect(() => {
    if (checkInDate && !checkInInputFocused) {
      setCheckInInputValue(formatDateForInput(checkInDate));
    }
  }, [checkInDate, checkInInputFocused]);

  useEffect(() => {
    if (checkOutDate && !checkOutInputFocused) {
      setCheckOutInputValue(formatDateForInput(checkOutDate));
    }
  }, [checkOutDate, checkOutInputFocused]);

  // Effect to handle initial date selection and pricing calculation
  useEffect(() => {
    if (initialCheckIn && initialCheckOut) {
      // If both dates are provided, close the calendar and trigger pricing
      setShowCalendar(false);
      updateSelectingCheckIn(true); // Reset for next time
    }
  }, [initialCheckIn, initialCheckOut, initialGuests]);
  const [pricing, setPricing] = useState<PricingData | null>(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [pricingError, setPricingError] = useState("");

  // Fetch property data to get maxGuests
  const { data: property } = useQuery<Property>({
    queryKey: ['/api/properties', propertyId],
    queryFn: async () => {
      const response = await fetch(`/api/properties/${propertyId}`);
      if (!response.ok) throw new Error('Failed to fetch property');
      return response.json();
    }
  });

  // Fetch calendar data for the current month and next month (for dual-month view)
  useEffect(() => {
    const fetchCalendarData = async () => {
      // Create cache keys for both months
      const cacheKey1 = `${propertyId}-${format(currentMonth, 'yyyy-MM')}`;
      const cacheKey2 = `${propertyId}-${format(addMonths(currentMonth, 1), 'yyyy-MM')}`;
      
      // Check cache for both months
      const cached1 = calendarCache.get(cacheKey1);
      const cached2 = calendarCache.get(cacheKey2);
      const now = Date.now();
      
      // If both months are cached and still valid, use them
      if (cached1 && cached2 && 
          (now - cached1.timestamp) < CACHE_DURATION && 
          (now - cached2.timestamp) < CACHE_DURATION) {
        // Combine cached data from both months
        const combinedData = [...cached1.data, ...cached2.data];
        setCalendarData(combinedData);
        setCalendarDataLoaded(true);
        console.log('Using cached calendar data for both months');
        return;
      }

      setLoadingCalendar(true);
      try {
        const startDate = startOfMonth(currentMonth);
        // Fetch two months of data to support dual-month view
        const endDate = endOfMonth(addMonths(currentMonth, 1));
        
        const response = await fetch(`/api/properties/${propertyId}/calendar?start=${format(startDate, 'yyyy-MM-dd')}&end=${format(endDate, 'yyyy-MM-dd')}`)
          .catch(fetchError => {
            // Handle fetch network errors
            console.warn('Network error loading calendar data:', fetchError);
            throw new Error('Network error');
          });
        
        if (response.ok) {
          const data = await response.json();
          setCalendarData(data);
          setCalendarDataLoaded(true);
          
          // Update cache for both months
          const month1Data = data.filter((d: CalendarSlot) => d.date.startsWith(format(currentMonth, 'yyyy-MM')));
          const month2Data = data.filter((d: CalendarSlot) => d.date.startsWith(format(addMonths(currentMonth, 1), 'yyyy-MM')));
          
          if (month1Data.length > 0) {
            calendarCache.set(cacheKey1, { data: month1Data, timestamp: now });
          }
          if (month2Data.length > 0) {
            calendarCache.set(cacheKey2, { data: month2Data, timestamp: now });
          }
          
          console.log('Calendar data loaded:', data.length, 'days');
        } else {
          // Don't log non-2xx responses as errors since they're handled
          setCalendarData([]);
          setCalendarDataLoaded(true);
        }
      } catch (error: any) {
        // Only log unexpected errors
        if (!error?.message?.includes('Network error') && 
            !error?.message?.includes('Failed to fetch')) {
          console.warn('Error loading calendar data:', error);
        }
        setCalendarData([]);
        setCalendarDataLoaded(true);
      } finally {
        setLoadingCalendar(false);
      }
    };

    fetchCalendarData();
  }, [propertyId, currentMonth]);

  // Fetch pricing when both dates are selected
  useEffect(() => {
    const fetchPricing = async (retryCount = 0) => {
      if (!checkInDate || !checkOutDate) {
        setPricing(null);
        setPricingError("");
        return;
      }

      setLoadingPricing(true);
      setPricingError("");
      
      try {
        const checkIn = format(checkInDate, 'yyyy-MM-dd');
        const checkOut = format(checkOutDate, 'yyyy-MM-dd');
        
        const response = await fetch(`/api/pricing/${propertyId}?checkIn=${checkIn}&checkOut=${checkOut}&guests=${guests}`)
          .catch(fetchError => {
            // Handle fetch network errors
            console.warn('Network error loading pricing data:', fetchError);
            throw new Error('Network error');
          });
        
        if (response.ok) {
          const pricingData = await response.json();
          if (pricingData.available !== false) {
            setPricing(pricingData);
          } else {
            setPricingError("Pricing not available for selected dates");
            setPricing(null);
          }
        } else if (response.status === 400) {
          // Handle 400 errors (unavailable/restricted dates) - surface the
          // server's specific message (e.g. closed for arrival/departure).
          try {
            const error = await response.json();
            setPricingError(error.message || "Pricing not available for selected dates");
          } catch {
            setPricingError("Pricing not available for selected dates");
          }
          setPricing(null);
        } else if (response.status >= 500 && retryCount < 2) {
          // Retry on server errors up to 2 times
          setTimeout(() => {
            fetchPricing(retryCount + 1);
          }, 1000 * (retryCount + 1)); // Exponential backoff
          return;
        } else {
          // Try to get error message from response
          try {
            const error = await response.json();
            setPricingError(error.message || "Failed to fetch pricing");
          } catch {
            setPricingError("Failed to fetch pricing");
          }
          setPricing(null);
        }
      } catch (error: any) {
        // Only log unexpected errors
        if (!error?.message?.includes('Network error') && 
            !error?.message?.includes('Failed to fetch')) {
          console.warn('Error loading pricing data:', error);
        }
        
        if (retryCount < 2) {
          // Retry on network errors
          setTimeout(() => {
            fetchPricing(retryCount + 1);
          }, 1000 * (retryCount + 1));
          return;
        }
        setPricingError(error.message || "Unable to fetch pricing");
        setPricing(null);
      } finally {
        setLoadingPricing(false);
      }
    };

    fetchPricing();
  }, [checkInDate, checkOutDate, guests, propertyId]);

  // Get availability for a specific date
  const getDateAvailability = (date: Date | null) => {
    if (date === null) return null;
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendarData.find(slot => slot.date === dateStr);
  };

  // Check if date is available
  const isDateAvailable = (date: Date | null) => {
    if (date === null) return false;
    const availability = getDateAvailability(date);
    return availability?.available || false;
  };

  // Uplisting restriction helpers: dates can be closed for arrival (no check-in)
  // or closed for departure (no check-out).
  const isClosedForArrival = (date: Date | null) => {
    if (date === null) return false;
    return getDateAvailability(date)?.closed_for_arrival === true;
  };
  const isClosedForDeparture = (date: Date | null) => {
    if (date === null) return false;
    return getDateAvailability(date)?.closed_for_departure === true;
  };

  // Get minimum stay for a single date - defaults to 2 when data is missing.
  const getMinimumStay = (date: Date | null) => {
    if (date === null) return 2;
    const availability = getDateAvailability(date);
    // Use nullish coalescing (??) so we respect explicit 1-night minimums.
    return availability?.minimum_stay ?? 2;
  };

  // Get the strictest minimum stay across every NIGHT in the [checkIn, checkOut) range.
  // This matches the server-side check, which prevented bookings where the check-in
  // day had a low minimum but a later night in the stay required more nights.
  const getMaxMinimumStayInRange = (checkIn: Date, checkOut: Date) => {
    const start = new Date(checkIn);
    start.setHours(0, 0, 0, 0);
    const end = new Date(checkOut);
    end.setHours(0, 0, 0, 0);

    let max = 1; // allow genuine 1-night stays where Uplisting permits
    const cursor = new Date(start);
    while (cursor < end) {
      const dayMin = getMinimumStay(cursor);
      if (dayMin > max) max = dayMin;
      cursor.setDate(cursor.getDate() + 1);
    }
    return max;
  };

  // Find the next booked date after a given check-in date
  const findNextBookingDate = (checkInDate: Date): Date | null => {
    if (!calendarDataLoaded) return null;
    
    const futureSlots = calendarData.filter(slot => 
      isAfter(parseISO(slot.date), checkInDate) && !slot.available
    );
    
    if (futureSlots.length === 0) return null;
    
    // Sort by date and return the earliest booking
    const sortedSlots = futureSlots.sort((a, b) => 
      compareAsc(parseISO(a.date), parseISO(b.date))
    );
    
    return parseISO(sortedSlots[0].date);
  };

  // Check if a checkout date would conflict with future bookings
  const isValidCheckoutDate = (checkoutDate: Date): boolean => {
    if (!checkInDate || !calendarDataLoaded) return true;
    
    const nextBooking = findNextBookingDate(checkInDate);
    if (!nextBooking) return true;
    
    // Checkout date can be ON the same day as the next booking (same-day turnover)
    // One guest checks out in the morning, another checks in that afternoon
    // So checkout is valid if it's before OR on the same day as the next booking
    return isBefore(checkoutDate, nextBooking) || isSameDay(checkoutDate, nextBooking);
  };

  // Check if date is selectable (today or future, available, and respects minimum stay)
  // Uses refs to always get latest state values (fixes stale closure issues)
  const isDateSelectable = (date: Date | null) => {
    if (date === null) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Create a normalized copy to avoid mutating the original date
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    
    // Past dates are never selectable
    if (normalizedDate < today) return false;
    
    // IMPORTANT: Don't allow date selection until calendar data is loaded
    // This ensures we properly enforce minimum stay requirements from Uplisting
    if (!calendarDataLoaded) {
      return false;
    }
    
    // Use refs to get the LATEST state values (not stale closure values)
    const currentSelectingCheckIn = selectingCheckInRef.current;
    const currentCheckInDate = checkInDateRef.current;
    
    // When selecting check-out date, special rules apply
    if (!currentSelectingCheckIn && currentCheckInDate) {
      const daysDiff = Math.ceil((normalizedDate.getTime() - currentCheckInDate.getTime()) / (1000 * 60 * 60 * 24));

      // Check-out date must satisfy the strictest minimum stay across EVERY night
      // in the candidate stay range (matches the server's rolling min-stay check).
      if (daysDiff > 0) {
        const minStay = getMaxMinimumStayInRange(currentCheckInDate, normalizedDate);
        if (daysDiff < minStay) {
          return false;
        }
      } else {
        return false;
      }
      
      // For checkout selection: Allow the turnover day (next booking's check-in)
      // This enables same-day turnovers where one guest checks out and another checks in
      const nextBooking = findNextBookingDate(currentCheckInDate);
      if (nextBooking && isSameDay(normalizedDate, nextBooking)) {
        // This is the turnover day - it's valid as a checkout date
        // even though it's marked unavailable (for the incoming guest)
        // ...unless the owner has closed it for departures in Uplisting.
        return !isClosedForDeparture(normalizedDate);
      }
      
      // For other dates during checkout selection, they must be available
      if (!isDateAvailable(normalizedDate)) return false;
      
      // Check-out date must not conflict with future bookings
      if (!isValidCheckoutDate(normalizedDate)) {
        return false;
      }

      // Uplisting restriction: cannot check OUT on a closed-for-departure date
      if (isClosedForDeparture(normalizedDate)) return false;
    } else {
      // When selecting check-in, date must be available
      if (!isDateAvailable(normalizedDate)) return false;

      // Uplisting restriction: cannot check IN on a closed-for-arrival date
      if (isClosedForArrival(normalizedDate)) return false;
    }
    
    return true;
  };

  // Handle date selection
  // Uses refs to get latest state values (fixes stale closure issues)
  const handleDateSelect = (date: Date) => {
    if (!isDateSelectable(date)) return;

    // Use refs to get the LATEST state values
    const currentSelectingCheckIn = selectingCheckInRef.current;
    const currentCheckInDate = checkInDateRef.current;

    if (currentSelectingCheckIn) {
      setCheckIn(date, 'calendar');
      setCheckOut(null, 'calendar');
      updateSelectingCheckIn(false);
      
      // If selecting check-in, auto-advance to check-out selection
      // but don't close calendar yet
    } else {
      // Selecting check-out
      if (currentCheckInDate && date > currentCheckInDate) {
        setCheckOut(date, 'calendar');
        updateSelectingCheckIn(true);
        setShowCalendar(false); // Close calendar after both dates selected
        document.body.style.overflow = 'auto';
      } else {
        // If selected date is before check-in, make it the new check-in
        setCheckIn(date, 'calendar');
        setCheckOut(null, 'calendar');
        updateSelectingCheckIn(false);
      }
    }
  };

  // Format input with automatic slash insertion
  const formatDateInput = (input: string): string => {
    // Remove all non-numeric characters
    const numbers = input.replace(/\D/g, '');
    
    // Add slashes automatically
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
    }
  };

  // Create masked input display showing format guide as user types
  const getMaskedInputDisplay = (value: string, isFocused: boolean): string => {
    if (!isFocused || !value) return value;
    
    const template = 'DD/MM/YYYY';
    const formatted = formatDateInput(value);
    const numbers = value.replace(/\D/g, '');
    
    // Build the masked display
    let result = '';
    let numberIndex = 0;
    
    if (numbers.length === 0) {
      return '';
    }
    
    // Handle day part (DD)
    if (numbers.length === 1) {
      result = numbers[0] + 'D/MM/YYYY';
    } else if (numbers.length === 2) {
      result = numbers.slice(0, 2) + '/MM/YYYY';
    } else if (numbers.length === 3) {
      result = numbers.slice(0, 2) + '/' + numbers[2] + 'M/YYYY';
    } else if (numbers.length === 4) {
      result = numbers.slice(0, 2) + '/' + numbers.slice(2, 4) + '/YYYY';
    } else if (numbers.length === 5) {
      result = numbers.slice(0, 2) + '/' + numbers.slice(2, 4) + '/' + numbers[4] + 'YYY';
    } else if (numbers.length === 6) {
      result = numbers.slice(0, 2) + '/' + numbers.slice(2, 4) + '/' + numbers.slice(4, 6) + 'YY';
    } else if (numbers.length === 7) {
      result = numbers.slice(0, 2) + '/' + numbers.slice(2, 4) + '/' + numbers.slice(4, 7) + 'Y';
    } else if (numbers.length >= 8) {
      result = numbers.slice(0, 2) + '/' + numbers.slice(2, 4) + '/' + numbers.slice(4, 8);
    }
    
    return result;
  };

  // Handle date input parsing
  const parseDateInput = (input: string): Date | null => {
    if (!input) return null;
    
    // Try to parse DD/MM/YYYY format
    const parsed = parse(input, 'd/M/yyyy', new Date());
    if (isValid(parsed)) {
      // Normalize to midnight to match calendar dates
      parsed.setHours(0, 0, 0, 0);
      return parsed;
    }
    return null;
  };

  // Handle check-in input changes
  const handleCheckInInputChange = (value: string) => {
    const formattedValue = formatDateInput(value);
    setCheckInInputValue(formattedValue);
    
    // Only try to parse if we have a complete date (DD/MM/YYYY)
    if (formattedValue.length === 10) {
      const parsedDate = parseDateInput(formattedValue);
      if (parsedDate && isDateSelectable(parsedDate)) {
        setCheckIn(parsedDate, 'input');
        setCheckOut(null, 'input');
        setCurrentMonth(parsedDate); // Navigate to the entered date's month
      }
    }
  };

  // Handle checkout input changes  
  const handleCheckOutInputChange = (value: string) => {
    const formattedValue = formatDateInput(value);
    setCheckOutInputValue(formattedValue);
    
    // Only try to parse if we have a complete date (DD/MM/YYYY)
    if (formattedValue.length === 10) {
      const parsedDate = parseDateInput(formattedValue);
      if (parsedDate && checkInDate && parsedDate > checkInDate && isValidCheckoutDate(parsedDate) && !isClosedForDeparture(parsedDate)) {
        setCheckOut(parsedDate, 'input');
      }
    }
  };

  // Handle input field focus
  const handleCheckInFocus = () => {
    setCheckInInputFocused(true);
    updateSelectingCheckIn(true);
    if (!showCalendar) {
      setShowCalendar(true);
      document.body.style.overflow = 'hidden';
    }
  };

  const handleCheckOutFocus = () => {
    if (checkInDate) {
      setCheckOutInputFocused(true);
      updateSelectingCheckIn(false);
      if (!showCalendar) {
        setShowCalendar(true);
        document.body.style.overflow = 'hidden';
      }
    }
  };

  // Handle input field blur
  const handleInputBlur = () => {
    setCheckInInputFocused(false);
    setCheckOutInputFocused(false);
  };

  // Navigate calendar months
  const goToPrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
    setCalendarDataLoaded(false); // Reset loading state for new month
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
    setCalendarDataLoaded(false); // Reset loading state for new month
  };

  // Get calendar days for a specific month (Airbnb style - only current month dates)
  const getCalendarDays = (month = currentMonth) => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    
    // Get all dates in the current month
    const monthDates = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Calculate how many empty slots we need at the beginning
    const startDayOfWeek = monthStart.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Create array with empty slots at the beginning, then month dates
    const calendarDays: (Date | null)[] = [];
    
    // Add empty slots for days before the month starts
    for (let i = 0; i < startDayOfWeek; i++) {
      calendarDays.push(null);
    }
    
    // Add all the dates in the month
    calendarDays.push(...monthDates);
    
    return calendarDays;
  };

  // Render a single month calendar
  const renderMonth = (month: Date, isSecondMonth = false) => {
    const days = getCalendarDays(month);
    
    return (
      <div className="flex-1 min-w-[280px]">
        {/* Month header - only show for first month on mobile, both on larger screens */}
        <div className={`flex items-center justify-between mb-4 ${isSecondMonth ? 'hidden md:flex' : 'flex'}`}>
          {/* Navigation buttons only on first month */}
          {!isSecondMonth && (
            <button
              onClick={goToPrevMonth}
              className="p-2 hover:bg-muted rounded"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <h4 className={`font-semibold ${isSecondMonth ? 'flex-1 text-center' : ''}`}>
            {format(month, 'MMMM yyyy')}
          </h4>
          {/* Navigation buttons - next only on second month (larger screens) or first month (mobile) */}
          {!isSecondMonth ? (
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-muted rounded md:hidden"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-muted rounded"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
          {/* Spacer for first month on larger screens */}
          {!isSecondMonth && (
            <div className="hidden md:block w-10" />
          )}
        </div>
        {/* Days of week header */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="text-center text-sm font-medium text-foreground/70 p-2">
              {day}
            </div>
          ))}
        </div>
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((date, index) => {
            // Handle empty slots (null values)
            if (date === null) {
              return (
                <div key={`empty-${index}`} className="p-2 text-sm">
                  {/* Empty slot - no content */}
                </div>
              );
            }

            const isCurrentMonth = isSameMonth(date, month);
            const isCheckInDate = checkInDate && isSameDay(date, checkInDate);
            const isCheckOutDate = checkOutDate && isSameDay(date, checkOutDate);
            const isAvailable = isDateSelectable(date);
            const isInRange = checkInDate && checkOutDate && date > checkInDate && date < checkOutDate;
            const isRangeStart = isCheckInDate && checkOutDate;
            const isRangeEnd = isCheckOutDate && checkInDate;
            
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const dateOnly = new Date(date);
            dateOnly.setHours(0, 0, 0, 0);
            const isPastDate = dateOnly < today;
            
            // Check if this date is booked (unavailable in calendar data)
            const isBookedInCalendar = calendarDataLoaded && (() => {
              const calendarEntry = calendarData?.find(entry => 
                isSameDay(parseISO(entry.date), date)
              );
              return calendarEntry ? !calendarEntry.available : false;
            })();
            
            // Check if this is the turnover day (next booking's check-in date)
            // This date can be selected as checkout even though it's marked unavailable
            const nextBooking = checkInDate ? findNextBookingDate(checkInDate) : null;
            const isTurnoverDay = !selectingCheckIn && checkInDate && nextBooking && 
                                  isSameDay(date, nextBooking) && isAfter(date, checkInDate);
            
            // Check if this date would violate minimum stay IF selected as checkout
            // This is independent of whether it's booked or not - just checking the day count
            // BUT: don't apply minimum stay logic to dates within an already selected range
            const wouldViolateMinStay = !selectingCheckIn && checkInDate && !checkOutDate && (() => {
              const daysDiff = Math.ceil((date.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
              if (daysDiff <= 0) return false;
              // Use rolling min-stay across the candidate range so we match the server check.
              const minStay = getMaxMinimumStayInRange(checkInDate, date);
              return daysDiff < minStay;
            })();
            
            // A date is blocked by minimum stay if it WOULD violate min stay AND is NOT booked
            // We only show tooltip for available dates that can't be selected due to min stay
            const isBlockedByMinStay = calendarDataLoaded && wouldViolateMinStay && !isBookedInCalendar && !isTurnoverDay;

            // Uplisting arrival/departure restrictions: block + tooltip.
            // When choosing a check-in date, closed-for-arrival dates are blocked;
            // when choosing a check-out date, closed-for-departure dates are blocked.
            const dateClosedForArrival = calendarDataLoaded && isClosedForArrival(date);
            const dateClosedForDeparture = calendarDataLoaded && isClosedForDeparture(date);
            const isBlockedByRestriction = calendarDataLoaded && !isBookedInCalendar && (
              selectingCheckIn
                ? dateClosedForArrival
                : (dateClosedForDeparture && (!checkInDate || isAfter(date, checkInDate)))
            );
            const restrictionTooltip = selectingCheckIn
              ? 'Date closed for arrival'
              : 'Date closed for departure';
            
            // Check if this date is blocked by future bookings (Airbnb logic)
            // When check-in is selected, checkout dates after the next booking become unavailable
            // But the turnover day itself is NOT blocked
            const isBlockedByFutureBooking = !selectingCheckIn && checkInDate && calendarDataLoaded && (() => {
              if (!isAfter(date, checkInDate)) return false; // Only applies to dates after check-in
              if (isTurnoverDay) return false; // Turnover day is allowed as checkout
              return !isValidCheckoutDate(date); // Use our smart booking logic
            })();
            
            // Minimum stay shown in the tooltip is the strictest requirement across
            // the candidate stay range (check-in → this cell), matching the booking rule.
            const minimumStayNights = checkInDate
              ? getMaxMinimumStayInRange(checkInDate, date)
              : 2;
            
            // Determine which category this date falls into
            // Turnover day is NOT disabled - it's a valid checkout option
            const isDisabled = !isCurrentMonth || isPastDate || (isBookedInCalendar && !isTurnoverDay) || isBlockedByFutureBooking;
            
            return (
              <div key={date.toISOString()} className="relative group" style={{ isolation: 'isolate' }}>
                {!isCurrentMonth ? (
                  // Dates from other months - just show greyed out
                  (<div className="w-full p-2 text-sm text-gray-300">
                    {format(date, 'd')}
                  </div>)
                ) : isDisabled ? (
                  // PAST, BOOKED, or BLOCKED BY FUTURE BOOKING dates: disabled 
                  (<button
                    disabled
                    className={`
                      w-full p-2 text-sm rounded relative transition-colors duration-200
                      text-foreground/50 cursor-not-allowed opacity-50
                      ${isToday(date) ? 'font-bold' : ''}
                    `}
                  >
                    <span className="relative inline-block">
                      {format(date, 'd')}
                      {(isBookedInCalendar || isPastDate) && (
                        <span 
                          className="absolute left-0 right-0 top-1/2 h-[2px] bg-toka-primary" 
                          style={{ transform: 'translateY(-50%)' }}
                        />
                      )}
                    </span>
                  </button>)
                ) : isBlockedByRestriction ? (
                  // CLOSED FOR ARRIVAL/DEPARTURE: shows tooltip on hover
                  (<>
                    <button
                      onClick={(e) => e.preventDefault()}
                      className={`
                        w-full p-2 text-sm rounded relative transition-colors duration-200
                        text-foreground/60 hover:text-foreground/60 cursor-not-allowed
                        ${isToday(date) ? 'font-bold' : ''}
                      `}
                    >
                      {format(date, 'd')}
                    </button>
                    {/* Tooltip using Tailwind group hover */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 
                                    hidden group-hover:block bg-gray-900 text-white 
                                    text-xs rounded-md px-2 py-1 whitespace-nowrap z-50">
                      {restrictionTooltip}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-0.5">
                        <div className="w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900"></div>
                      </div>
                    </div>
                  </>)
                ) : isBlockedByMinStay ? (
                  // BLOCKED BY MIN STAY: shows tooltip on hover
                  (<>
                    <button
                      onClick={(e) => e.preventDefault()}
                      className={`
                        w-full p-2 text-sm rounded relative transition-colors duration-200
                        text-foreground hover:text-foreground cursor-not-allowed
                        ${isToday(date) ? 'font-bold' : ''}
                      `}
                    >
                      {format(date, 'd')}
                    </button>
                    {/* Tooltip using Tailwind group hover */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 
                                    hidden group-hover:block bg-gray-900 text-white 
                                    text-xs rounded-md px-2 py-1 whitespace-nowrap z-50">
                      {minimumStayNights}-night minimum
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-0.5">
                        <div className="w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900"></div>
                      </div>
                    </div>
                  </>)
                ) : (
                  // AVAILABLE dates: fully clickable
                  // onMouseDown preventDefault stops blur event from stealing the click
                  (<button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleDateSelect(date)}
                    style={{ position: 'relative', zIndex: 10 }}
                    className={`
                      w-full p-2 text-sm rounded transition-colors duration-200
                      hover:bg-muted pointer-events-auto
                      ${(isCheckInDate || isCheckOutDate) ? 'bg-primary text-white hover:bg-accent' : ''}
                      ${isInRange ? 'bg-[color-mix(in_srgb,var(--toka-primary)_20%,transparent)] text-foreground' : ''}
                      ${isRangeStart ? 'rounded-r-none' : ''}
                      ${isRangeEnd ? 'rounded-l-none' : ''}
                      ${isToday(date) ? 'font-bold' : ''}
                    `}
                  >
                    {format(date, 'd')}
                  </button>)
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Format date for display
  const formatDateForDisplay = (date: Date | null) => {
    if (!date) return '';
    return format(date, 'MMM d, yyyy');
  };

  // Format date for input field (dd/MM/yyyy)
  const formatDateForInput = (date: Date | null) => {
    if (!date) return '';
    return format(date, 'dd/MM/yyyy');
  };

  // Helper function to set check-in date from different sources
  const setCheckIn = (date: Date | null, source: 'calendar' | 'input') => {
    updateCheckInDate(date);
    if (source === 'calendar' || !checkInInputFocused) {
      setCheckInInputValue(date ? formatDateForInput(date) : '');
    }
  };

  // Helper function to set check-out date from different sources
  const setCheckOut = (date: Date | null, source: 'calendar' | 'input') => {
    setCheckOutDate(date);
    if (source === 'calendar' || !checkOutInputFocused) {
      setCheckOutInputValue(date ? formatDateForInput(date) : '');
    }
  };

  // Handle clear dates
  const handleClearDates = () => {
    setCheckIn(null, 'calendar');
    setCheckOut(null, 'calendar');
    updateSelectingCheckIn(true);
  };

  // Handle booking submission
  const handleBookNow = () => {
    if (!checkInDate || !checkOutDate || !pricing) return;
    
    // Validate minimum stay requirement using the strictest rule across every
    // night in the stay (matches the server's rolling min-stay check so we never
    // send a customer to checkout with a date range the server will reject).
    const nightsSelected = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
    const minStay = getMaxMinimumStayInRange(checkInDate, checkOutDate);

    if (nightsSelected < minStay) {
      alert(`This property requires a minimum stay of ${minStay} nights for these dates. You have selected ${nightsSelected} night${nightsSelected !== 1 ? 's' : ''}.`);
      return;
    }

    // Enforce Uplisting arrival/departure restrictions (matches server checks).
    if (isClosedForArrival(checkInDate)) {
      alert(`Check-in isn't available on ${format(checkInDate, 'MMM d, yyyy')} — this date is closed for arrivals. Please choose a different check-in date.`);
      return;
    }
    if (isClosedForDeparture(checkOutDate)) {
      alert(`Check-out isn't available on ${format(checkOutDate, 'MMM d, yyyy')} — this date is closed for departures. Please choose a different check-out date.`);
      return;
    }
    
    const checkIn = format(checkInDate, 'yyyy-MM-dd');
    const checkOut = format(checkOutDate, 'yyyy-MM-dd');
    
    window.location.href = `/checkout?propertyId=${propertyId}&checkIn=${checkIn}&checkOut=${checkOut}&guests=${guests}`;
  };

  return (
    <div className={`relative ${className}`}>
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Header Text */}
            <div>
              <p className="text-sm text-foreground">Add dates for prices</p>
            </div>

            {/* Date Selection */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium mb-1 text-foreground">Check in</label>
                <button
                  onClick={handleCheckInFocus}
                  data-state={showCalendar && selectingCheckIn ? 'open' : 'closed'}
                  className={`w-full p-2 border rounded-lg text-left ${bookingTriggerStyles}`}
                >
                  <div className="text-sm">
                    {checkInDate ? formatDateForDisplay(checkInDate) : 'Select date'}
                  </div>
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-foreground">Check out</label>
                <button
                  onClick={handleCheckOutFocus}
                  data-state={showCalendar && !selectingCheckIn ? 'open' : 'closed'}
                  className={`w-full p-2 border rounded-lg text-left ${bookingTriggerStyles}`}
                  disabled={!checkInDate}
                >
                  <div className="text-sm">
                    {checkOutDate ? formatDateForDisplay(checkOutDate) : 'Select date'}
                  </div>
                </button>
              </div>
            </div>

            {/* Minimum Stay Notice */}
            {checkInDate && !checkOutDate && calendarDataLoaded && (
              <div className="text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded px-3 py-2">
                {getMinimumStay(checkInDate)}-night minimum stay
              </div>
            )}

            {/* Guests Selection */}
            <div>
              <label className="block text-sm font-medium mb-1 text-foreground">Guests</label>
              <Select value={guests.toString()} onValueChange={(value) => setGuests(parseInt(value))}>
                <SelectTrigger className={`w-full ${bookingTriggerStyles}`}>
                  <div className="flex items-center space-x-2 text-foreground">
                    <Users className="h-4 w-4 text-foreground" />
                    <SelectValue className="text-foreground" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: property?.maxGuests || 8 }, (_, i) => i + 1).map(num => (
                    <SelectItem key={num} value={num.toString()}>
                      {num} guest{num !== 1 ? 's' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Pricing Summary */}
            {pricing && (
              <div className="space-y-2 pt-4 border-t text-foreground">
                <div className="flex justify-between text-sm">
                  <span>Per night (avg)</span>
                  <span>${pricing.averageNightlyRate}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Accommodation total</span>
                  <span>${pricing.totalPrice}</span>
                </div>
                {pricing.fees?.cleaningFee && (
                  <div className="flex justify-between text-sm">
                    <span>Cleaning fee</span>
                    <span>${pricing.fees.cleaningFee.toFixed(2)}</span>
                  </div>
                )}

                {pricing.fees?.extraGuestCharge != null && pricing.fees.extraGuestCharge > 0 && guests > 2 && (
                  <div className="flex justify-between text-sm">
                    <span>Extra guest charges</span>
                    <span>${(pricing.fees.extraGuestCharge * (guests - 2)).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold border-t pt-2">
                  <span>Total</span>
                  <span>${(
                    parseFloat(pricing.totalPrice) + 
                    (pricing.fees?.cleaningFee || 0) + 
                    (pricing.fees?.extraGuestCharge && pricing.fees.extraGuestCharge > 0 && guests > 2 ? pricing.fees.extraGuestCharge * (guests - 2) : 0)
                  ).toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Book Now Button */}
            <Button
              onClick={handleBookNow}
              className="w-full bg-primary hover:bg-accent text-white"
              disabled={!checkInDate || !checkOutDate || !pricing}
            >
              {loadingPricing ? 'Loading...' : 'Proceed to checkout'}
            </Button>

            {/* Payment Options */}
            <div className="mt-4 flex flex-col items-center justify-center text-sm text-toka-text">
              <span className="text-foreground text-center text-[12px]">Secure Payments</span>
              <div className="flex items-center gap-3 mt-2">
                <img 
                  src={stripeLogo} 
                  alt="Powered by Stripe" 
                  className="h-5 w-auto"
                  width={88}
                  height={20}
                />
              </div>
            </div>

            {/* Error Display */}
            {pricingError && (
              <div className="text-orange-600 text-sm text-center p-3 bg-orange-50 border border-orange-200 rounded">
                <div className="font-medium">Pricing Unavailable</div>
                <div className="text-xs mt-1">Please try different dates or contact us for assistance</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      {/* Calendar Popup - rendered via Portal to escape sticky container stacking context */}
      {showCalendar && createPortal(
        <div 
          className="fixed inset-0 z-[9999] flex items-start justify-center bg-black bg-opacity-50 p-4 pt-16 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCalendar(false);
              document.body.style.overflow = 'auto';
            }
          }}
        >
          <div className="bg-background border rounded-lg shadow-xl p-4 w-80 md:w-auto md:max-w-2xl mt-8 mb-8 relative">
            {/* Header */}
            <div className="mb-4">
              {/* Top row with title */}
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Select dates</h3>
              </div>
                
              {/* Check-in/Check-out Date Inputs */}
              <div className="flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-2 mb-2">
                  <div className="flex flex-col">
                    <label className="text-xs text-foreground/70 mb-1">CHECK-IN</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={checkInInputFocused ? checkInInputValue : (checkInDate ? format(checkInDate, 'd/M/yyyy') : '')}
                        onChange={(e) => handleCheckInInputChange(e.target.value)}
                        onFocus={handleCheckInFocus}
                        onBlur={handleInputBlur}
                        placeholder={checkInInputFocused ? '' : 'Add date'}
                        className={`px-3 py-2 text-sm border rounded bg-toka-secondary placeholder:text-white ${
                          selectingCheckIn ? 'border-[var(--toka-primary)]' : 'border-gray-300'
                        } hover:border-[var(--toka-primary)] min-w-[90px] text-left focus:outline-none focus:border-[var(--toka-primary)] focus:ring-0`}
                        style={{ color: 'transparent', caretColor: 'white' }}
                      />
                      {checkInInputFocused && (
                        <div className="absolute inset-0 px-3 py-2 pointer-events-none text-sm">
                          {checkInInputValue ? (
                            <>
                              <span className="text-white">{checkInInputValue}</span>
                              <span className="text-white">
                                {getMaskedInputDisplay(checkInInputValue, true).slice(checkInInputValue.length)}
                              </span>
                            </>
                          ) : (
                            <span className="text-white">DD/MM/YYYY</span>
                          )}
                        </div>
                      )}
                      {!checkInInputFocused && (
                        <div className="absolute inset-0 px-3 py-2 pointer-events-none text-sm text-white">
                          {checkInDate ? format(checkInDate, 'd/M/yyyy') : ''}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs text-foreground/70 mb-1">CHECKOUT</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={checkOutInputFocused ? checkOutInputValue : (checkOutDate ? format(checkOutDate, 'd/M/yyyy') : '')}
                        onChange={(e) => handleCheckOutInputChange(e.target.value)}
                        onFocus={handleCheckOutFocus}
                        onBlur={handleInputBlur}
                        placeholder={checkOutInputFocused ? '' : 'Add date'}
                        disabled={!checkInDate}
                        className={`px-3 py-2 text-sm border rounded bg-toka-secondary placeholder:text-white ${
                          !selectingCheckIn && checkInDate ? 'border-[var(--toka-primary)]' : 'border-gray-300'
                        } enabled:hover:border-[var(--toka-primary)] min-w-[90px] text-left focus:outline-none focus:border-[var(--toka-primary)] focus:ring-0 ${
                          !checkInDate ? 'cursor-not-allowed' : ''
                        }`}
                        style={{ color: 'transparent', caretColor: 'white' }}
                      />
                      {checkOutInputFocused && (
                        <div className="absolute inset-0 px-3 py-2 pointer-events-none text-sm">
                          {checkOutInputValue ? (
                            <>
                              <span className="text-white">{checkOutInputValue}</span>
                              <span className="text-white">
                                {getMaskedInputDisplay(checkOutInputValue, true).slice(checkOutInputValue.length)}
                              </span>
                            </>
                          ) : (
                            <span className="text-white">DD/MM/YYYY</span>
                          )}
                        </div>
                      )}
                      {!checkOutInputFocused && (
                        <div className="absolute inset-0 px-3 py-2 pointer-events-none text-sm text-white">
                          {checkOutDate ? format(checkOutDate, 'd/M/yyyy') : ''}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Minimum stay notice */}
                {checkInDate && calendarDataLoaded && (
                  <p className="text-sm text-foreground">
                    Minimum stay: {getMinimumStay(checkInDate)} night{getMinimumStay(checkInDate) > 1 ? 's' : ''}
                  </p>
                )}
              </div>

            {/* Loading indicator */}
            {loadingCalendar && (
              <div className="text-sm text-foreground/70 text-center mb-2">
                Checking availability...
              </div>
            )}

            {/* Calendar Container - Single month on mobile, dual months on larger screens */}
            <div className="flex flex-col md:flex-row md:space-x-6">
              {/* First Month */}
              <div className="md:hidden">
                {renderMonth(currentMonth)}
              </div>
              
              {/* Dual Month Layout for larger screens */}
              <div className="hidden md:flex md:space-x-6">
                {renderMonth(currentMonth)}
                {renderMonth(addMonths(currentMonth, 1), true)}
              </div>
            </div>

            {/* Bottom buttons */}
            <div className="mt-4 flex justify-between">
              {/* Clear Button - only show if dates are selected */}
              {(checkInDate || checkOutDate) ? (
                <button
                  onClick={handleClearDates}
                  className={calendarActionStyles}
                >
                  Clear dates
                </button>
              ) : (
                <div></div> // Empty div to maintain spacing
              )}
              
              {/* Close Button */}
              <button
                onClick={() => {
                  setShowCalendar(false);
                  document.body.style.overflow = 'auto';
                }}
                className={calendarActionStyles}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}